import { format, addDays, addWeeks, startOfYear, isWithinInterval, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';

// Import des vraies définitions des types d'absences
const ABSENCE_TYPES = {
  CP: { label: 'CA' },
  JTL: { label: 'JTL' },
  MALADIE: { label: 'Arrêt maladie' },
  FORMATION: { label: 'Formation' },
  CM: { label: 'CM' },
  TPS: { label: 'Temps partiel' },
  RECUP: { label: 'Récupération' },
  RECUP_ASTREINTE: { label: 'Récup astreinte' },
  MEDICAL: { label: 'Visite médicale' },
  AUTRE: { label: 'Autre' }
};

/**
 * Service de détection des conflits dans les astreintes
 */
export class ConflictDetectionService {

  /**
   * Obtenir le label d'affichage pour un type d'absence
   */
  static getAbsenceDisplayLabel(absenceType) {
    if (!absenceType) return 'Type inconnu';
    
    // Normaliser le type (majuscules)
    const normalizedType = absenceType.toUpperCase();
    
    
    // Retourner le label correspondant ou le type original si pas trouvé
    return ABSENCE_TYPES[normalizedType]?.label || absenceType;
  }
  
  /**
   * Vérifier la conformité de toutes les astreintes pour une année donnée
   * @param {number} year - L'année à vérifier
   * @param {Array} onCalls - Liste des astreintes
   * @param {Array} technicians - Liste des techniciens
   * @param {Array} skills - Liste des compétences
   * @param {Array} absences - Liste des absences
   * @param {Array} holidays - Liste des jours fériés
   * @returns {Promise<Object>} Résultat de la vérification
   */
  static async checkCompliance(year, onCalls, technicians, skills, absences, holidays) {
    const results = {
      totalChecks: 0,
      conflicts: [],
      warnings: [],
      suggestions: [],
      summary: {
        criticalErrors: 0,
        warnings: 0,
        suggestions: 0,
        coverageGaps: 0,
        restPeriodViolations: 0,
        skillConflicts: 0,
        absenceConflicts: 0
      }
    };

    try {
      // 1. Générer les semaines de l'année
      const weeks = this.generateYearWeeks(year);
      results.totalChecks = weeks.length;

      // 2. Filtrer les astreintes de l'année pour les vérifications par semaine
      const yearOnCalls = onCalls.filter(onCall => {
        const onCallYear = new Date(onCall.startDate).getFullYear();
        return onCallYear === year;
      });

      // 3. Vérifier chaque semaine (seulement avec les astreintes de l'année)
      for (const week of weeks) {
        await this.checkWeekCompliance(week, yearOnCalls, technicians, skills, absences, holidays, results);
      }

      // 4. Vérifier les périodes de repos entre astreintes (avec toutes les astreintes pour détecter les conflits inter-années)
      this.checkRestPeriods(onCalls, technicians, results);

      // 5. Vérifier la couverture globale des compétences (seulement l'année courante)
      this.checkSkillCoverage(weeks, yearOnCalls, skills, results);

      // 6. Analyser les équilibres de charge (seulement l'année courante)
      this.analyzeWorkloadBalance(yearOnCalls, technicians, year, results);

      // 7. Générer les suggestions d'amélioration
      this.generateSuggestions(results);

    } catch (error) {
      console.error('Erreur lors de la vérification de conformité:', error);
      results.conflicts.push({
        type: 'SYSTEM_ERROR',
        severity: 'critical',
        message: 'Erreur système lors de la vérification',
        details: error.message
      });
    }

    return results;
  }

  /**
   * Générer les 52 semaines d'une année (vendredi à jeudi)
   */
  static generateYearWeeks(year) {
    const weeks = [];
    let firstFriday = startOfYear(new Date(year, 0, 1));
    
    // Trouver le premier vendredi
    while (firstFriday.getDay() !== 5) {
      firstFriday = addDays(firstFriday, 1);
    }

    for (let week = 0; week < 52; week++) {
      const weekStart = addWeeks(firstFriday, week);
      const weekEnd = addDays(weekStart, 6);
      
      weeks.push({
        number: week + 1,
        startDate: weekStart,
        endDate: weekEnd,
        semester: weekStart.getMonth() < 6 ? 'S1' : 'S2'
      });
    }

    return weeks;
  }

  /**
   * Vérifier la conformité d'une semaine
   */
  static async checkWeekCompliance(week, onCalls, technicians, skills, absences, holidays, results) {
    // Trouver les astreintes de cette semaine
    const weekOnCalls = onCalls.filter(onCall => {
      const startDate = new Date(onCall.startDate);
      const endDate = new Date(onCall.endDate);
      
      return isWithinInterval(week.startDate, { start: startDate, end: endDate }) ||
             isWithinInterval(week.endDate, { start: startDate, end: endDate }) ||
             isWithinInterval(startDate, { start: week.startDate, end: week.endDate });
    });

    // 1. Vérifier les conflits de compétences (plusieurs techniciens sur la même compétence)
    this.checkSkillConflicts(week, weekOnCalls, skills, results);

    // 2. Vérifier les absences
    for (const onCall of weekOnCalls) {
      await this.checkAbsenceConflicts(week, onCall, absences, results);
    }

    // 3. Vérifier la couverture des compétences
    this.checkWeekSkillCoverage(week, weekOnCalls, skills, results);

    // 4. Vérifier les jours fériés (recommandations)
    this.checkHolidayRecommendations(week, weekOnCalls, holidays, results);
  }

  /**
   * Vérifier les conflits de compétences dans une semaine
   */
  static checkSkillConflicts(week, weekOnCalls, skills, results) {
    const skillAssignments = {};

    // Grouper par compétence
    weekOnCalls.forEach(onCall => {
      (onCall.skills || []).forEach(skillId => {
        if (!skillAssignments[skillId]) {
          skillAssignments[skillId] = [];
        }
        skillAssignments[skillId].push(onCall);
      });
    });

    // Vérifier les conflits
    Object.entries(skillAssignments).forEach(([skillId, assignments]) => {
      if (assignments.length > 1) {
        const skill = skills.find(s => s.id === parseInt(skillId));
        results.conflicts.push({
          type: 'SKILL_CONFLICT',
          severity: 'critical',
          weekNumber: week.number,
          weekPeriod: `${format(week.startDate, 'dd/MM', { locale: fr })} au ${format(week.endDate, 'dd/MM', { locale: fr })}`,
          message: `Conflit sur la compétence ${skill?.name || skillId}`,
          details: `${assignments.length} techniciens assignés simultanément`,
          skillId: parseInt(skillId),
          weekStart: week.startDate, // Date de début de semaine pour navigation
          onCalls: assignments.map(oc => oc.id),
          onCallId: assignments[0].id // Premier ID d'astreinte pour navigation
        });
        results.summary.skillConflicts++;
      }
    });
  }

  /**
   * Vérifier les conflits avec les absences
   */
  static async checkAbsenceConflicts(week, onCall, absences, results) {
    const technicianAbsences = absences.filter(abs => abs.technicianId === onCall.technicianId);
    
    
    for (const absence of technicianAbsences) {
      const absenceStart = new Date(absence.startDate);
      const absenceEnd = new Date(absence.endDate);
      const onCallStart = new Date(onCall.startDate);
      const onCallEnd = new Date(onCall.endDate);

      // Vérifier le chevauchement
      // Créer les dates avec fuseau horaire explicite pour éviter les décalages UTC
      const onCallStartTime = new Date(onCallStart.toISOString().split('T')[0] + 'T00:00:00');
      const onCallEndTime = new Date(onCallEnd.toISOString().split('T')[0] + 'T23:59:59');
      const absenceStartTime = new Date(absenceStart.toISOString().split('T')[0] + 'T00:00:00');
      const absenceEndTime = new Date(absenceEnd.toISOString().split('T')[0] + 'T23:59:59');

      if (isWithinInterval(onCallStartTime, { start: absenceStartTime, end: absenceEndTime }) ||
          isWithinInterval(onCallEndTime, { start: absenceStartTime, end: absenceEndTime }) ||
          isWithinInterval(absenceStartTime, { start: onCallStartTime, end: onCallEndTime })) {
        
        // Déterminer la sévérité selon le type d'absence
        const { severity, message } = this.getAbsenceSeverityAndMessage(absence.type);
        
        // Vérifier si l'absence a un bypass activé
        const bypassActivated = absence.bypassAbsenceValidation || false;
        
        // Si bypass activé, ignorer complètement cette absence
        if (bypassActivated) {
          continue;
        }
        
        // Ignorer les absences informatives (RECUP_ASTREINTE)
        if (severity === 'info') {
          continue;
        }
        
        results[severity === 'critical' ? 'conflicts' : 'warnings'].push({
          type: 'ABSENCE_CONFLICT',
          severity: severity,
          weekNumber: week.number,
          weekPeriod: `${format(week.startDate, 'dd/MM', { locale: fr })} au ${format(week.endDate, 'dd/MM', { locale: fr })}`,
          message: message,
          details: `Technicien en absence du ${format(absenceStart, 'dd/MM/yyyy', { locale: fr })} au ${format(absenceEnd, 'dd/MM/yyyy', { locale: fr })}`,
          technicianId: onCall.technicianId,
          onCallId: onCall.id,
          absenceId: absence.id,
          weekStart: week.startDate, // Date de début de semaine pour navigation
          absenceType: absence.type,
          absenceDisplayLabel: this.getAbsenceDisplayLabel(absence.type),
          bypassActivated: false
        });

        if (severity === 'critical') {
          results.summary.absenceConflicts++;
        }
      }
    }
  }

  /**
   * Déterminer la sévérité et le message selon le type d'absence
   */
  static getAbsenceSeverityAndMessage(absenceType) {
    const type = absenceType?.toUpperCase();
    const displayLabel = this.getAbsenceDisplayLabel(absenceType);
    
    switch (type) {
      case 'JTL':
        return {
          severity: 'warning',
          message: `Conflit avec absence ${displayLabel} (compatible mais à noter)`
        };
      
      case 'RECUP_ASTREINTE':
        return {
          severity: 'info',
          message: `${displayLabel} détectée (information seulement, non bloquante)`
        };
      
      case 'CA':
      case 'CP':
        return {
          severity: 'critical',
          message: `Conflit avec ${displayLabel} - BLOQUANT`
        };
      
      case 'ARRET MALADIE':
      case 'MALADIE':
        return {
          severity: 'critical',
          message: `Conflit avec ${displayLabel} - BLOQUANT`
        };
      
      case 'FORMATION':
        return {
          severity: 'critical',
          message: `Conflit avec ${displayLabel} - BLOQUANT`
        };
      
      case 'CM':
        return {
          severity: 'critical',
          message: `Conflit avec ${displayLabel} - BLOQUANT`
        };
      
      case 'RECUP':
        return {
          severity: 'critical',
          message: `Conflit avec ${displayLabel} - BLOQUANT`
        };
      
      case 'MEDICAL':
        return {
          severity: 'critical',
          message: `Conflit avec ${displayLabel} - BLOQUANT`
        };
      
      case 'AUTRE':
        return {
          severity: 'critical',
          message: `Absence de type "${displayLabel}" - Veuillez spécifier le type d'absence`
        };
      
      case 'TPS':
        return {
          severity: 'critical',
          message: `Conflit avec ${displayLabel} - BLOQUANT`
        };
      
      default:
        return {
          severity: 'critical',
          message: `Conflit avec absence ${displayLabel} - À vérifier`
        };
    }
  }

  /**
   * Vérifier la couverture des compétences pour une semaine
   */
  static checkWeekSkillCoverage(week, weekOnCalls, skills, results) {
    const coveredSkills = new Set();
    
    weekOnCalls.forEach(onCall => {
      (onCall.skills || []).forEach(skillId => {
        coveredSkills.add(skillId);
      });
    });

    const uncoveredSkills = skills.filter(skill => !coveredSkills.has(skill.id));
    
    if (uncoveredSkills.length > 0) {
      results.warnings.push({
        type: 'COVERAGE_GAP',
        severity: 'warning',
        weekNumber: week.number,
        weekPeriod: `${format(week.startDate, 'dd/MM', { locale: fr })} au ${format(week.endDate, 'dd/MM', { locale: fr })}`,
        weekStart: week.startDate, // Date de début de semaine pour navigation
        message: `Compétences non couvertes: ${uncoveredSkills.map(s => s.name).join(', ')}`,
        details: `${uncoveredSkills.length} compétence(s) sans technicien assigné`,
        uncoveredSkills: uncoveredSkills.map(s => s.id)
      });
      results.summary.coverageGaps++;
    }
  }

  /**
   * Vérifier les périodes de repos entre astreintes
   */
  static checkRestPeriods(onCalls, technicians, results) {
    const technicianOnCalls = {};
    
    // Grouper par technicien
    onCalls.forEach(onCall => {
      if (!technicianOnCalls[onCall.technicianId]) {
        technicianOnCalls[onCall.technicianId] = [];
      }
      technicianOnCalls[onCall.technicianId].push(onCall);
    });

    // Vérifier les périodes de repos pour chaque technicien
    Object.entries(technicianOnCalls).forEach(([technicianId, techOnCalls]) => {
      // Trier par date de début
      techOnCalls.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

      for (let i = 1; i < techOnCalls.length; i++) {
        const previousEnd = new Date(techOnCalls[i - 1].endDate);
        const currentStart = new Date(techOnCalls[i].startDate);
        const daysBetween = differenceInDays(currentStart, previousEnd);

        // Période de repos recommandée : au moins 1 semaine (7 jours)
        if (daysBetween < 7) {
          const technician = technicians[technicianId];
          results.warnings.push({
            type: 'REST_PERIOD_VIOLATION',
            severity: 'warning',
            message: `Période de repos insuffisante pour ${technician?.firstName} ${technician?.lastName}`,
            details: `Seulement ${daysBetween} jour(s) entre deux astreintes`,
            technicianId: parseInt(technicianId),
            onCallIds: [techOnCalls[i - 1].id, techOnCalls[i].id],
            daysBetween
          });
          results.summary.restPeriodViolations++;
        }
      }
    });
  }

  /**
   * Vérifier la couverture globale des compétences
   */
  static checkSkillCoverage(weeks, onCalls, skills, results) {
    const skillCoverage = {};
    
    skills.forEach(skill => {
      skillCoverage[skill.id] = {
        skill,
        coveredWeeks: 0,
        totalWeeks: weeks.length
      };
    });

    weeks.forEach(week => {
      const weekOnCalls = onCalls.filter(onCall => {
        const startDate = new Date(onCall.startDate);
        const endDate = new Date(onCall.endDate);
        
        return isWithinInterval(week.startDate, { start: startDate, end: endDate }) ||
               isWithinInterval(week.endDate, { start: startDate, end: endDate }) ||
               isWithinInterval(startDate, { start: week.startDate, end: week.endDate });
      });

      const coveredSkills = new Set();
      weekOnCalls.forEach(onCall => {
        (onCall.skills || []).forEach(skillId => {
          coveredSkills.add(skillId);
        });
      });

      coveredSkills.forEach(skillId => {
        if (skillCoverage[skillId]) {
          skillCoverage[skillId].coveredWeeks++;
        }
      });
    });

    // Analyser la couverture
    Object.values(skillCoverage).forEach(coverage => {
      const coveragePercent = (coverage.coveredWeeks / coverage.totalWeeks) * 100;
      
      if (coveragePercent < 90) {
        results.suggestions.push({
          type: 'LOW_SKILL_COVERAGE',
          severity: 'info',
          message: `Couverture faible pour ${coverage.skill.name}`,
          details: `Seulement ${coveragePercent.toFixed(1)}% des semaines couvertes (${coverage.coveredWeeks}/${coverage.totalWeeks})`,
          skillId: coverage.skill.id,
          coveragePercent: Math.round(coveragePercent)
        });
      }
    });
  }

  /**
   * Vérifier les périodes de repos entre astreintes (2 semaines minimum)
   */
  static checkRestPeriods(onCalls, technicians, results) {
    // Regrouper les astreintes par technicien
    const technicianOnCalls = {};
    
    onCalls.forEach(onCall => {
      if (!technicianOnCalls[onCall.technicianId]) {
        technicianOnCalls[onCall.technicianId] = [];
      }
      technicianOnCalls[onCall.technicianId].push(onCall);
    });
    
    // Vérifier chaque technicien
    Object.keys(technicianOnCalls).forEach(techId => {
      const techOnCalls = technicianOnCalls[techId];
      
      // Trier les astreintes par date de début
      techOnCalls.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
      
      // Vérifier les intervalles entre astreintes consécutives
      for (let i = 0; i < techOnCalls.length - 1; i++) {
        const current = techOnCalls[i];
        const next = techOnCalls[i + 1];
        
        const currentEndDate = new Date(current.endDate);
        const nextStartDate = new Date(next.startDate);
        
        // Calculer la différence en jours
        const daysBetween = differenceInDays(nextStartDate, currentEndDate);
        
        // Vérifier le repos minimum de 14 jours (2 semaines)
        if (daysBetween < 14) {
          const technician = technicians[techId];
          const technicianName = technician ? `${technician.firstName} ${technician.lastName}` : `Technicien ${techId}`;
          
          results.conflicts.push({
            type: 'REST_PERIOD_VIOLATION',
            severity: 'critical',
            message: `Période de repos insuffisante pour ${technicianName}`,
            details: `Seulement ${daysBetween} jour(s) entre deux astreintes (minimum requis: 14 jours)`,
            technicianId: parseInt(techId),
            currentOnCallId: current.id,
            nextOnCallId: next.id,
            currentEndDate: format(currentEndDate, 'dd/MM/yyyy', { locale: fr }),
            nextStartDate: format(nextStartDate, 'dd/MM/yyyy', { locale: fr }),
            daysBetween: daysBetween
          });
          
          results.summary.restPeriodViolations++;
        }
      }
    });
  }

  /**
   * Analyser l'équilibre de charge entre techniciens
   */
  static analyzeWorkloadBalance(onCalls, technicians, year, results) {
    const technicianWorkload = {};
    
    // Calculer la charge de travail par technicien
    Object.keys(technicians).forEach(techId => {
      technicianWorkload[techId] = {
        technician: technicians[techId],
        onCallCount: 0,
        totalDays: 0
      };
    });

    onCalls.forEach(onCall => {
      if (technicianWorkload[onCall.technicianId]) {
        technicianWorkload[onCall.technicianId].onCallCount++;
        const startDate = new Date(onCall.startDate);
        const endDate = new Date(onCall.endDate);
        technicianWorkload[onCall.technicianId].totalDays += differenceInDays(endDate, startDate) + 1;
      }
    });

    // Analyser l'équilibre
    const workloads = Object.values(technicianWorkload).filter(w => w.onCallCount > 0);
    if (workloads.length > 1) {
      const avgDays = workloads.reduce((sum, w) => sum + w.totalDays, 0) / workloads.length;
      const maxDays = Math.max(...workloads.map(w => w.totalDays));
      const minDays = Math.min(...workloads.map(w => w.totalDays));
      
      // Si l'écart est > 50% de la moyenne, suggérer un rééquilibrage
      if ((maxDays - minDays) > (avgDays * 0.5)) {
        const overloaded = workloads.filter(w => w.totalDays > avgDays * 1.25);
        const underloaded = workloads.filter(w => w.totalDays < avgDays * 0.75);
        
        if (overloaded.length > 0 || underloaded.length > 0) {
          results.suggestions.push({
            type: 'WORKLOAD_IMBALANCE',
            severity: 'info',
            message: 'Déséquilibre de charge détecté',
            details: `Écart de ${maxDays - minDays} jours entre techniciens (moyenne: ${Math.round(avgDays)} jours)`,
            overloaded: overloaded.map(w => ({ 
              technicianId: w.technician.id, 
              name: `${w.technician.firstName} ${w.technician.lastName}`,
              days: w.totalDays 
            })),
            underloaded: underloaded.map(w => ({ 
              technicianId: w.technician.id, 
              name: `${w.technician.firstName} ${w.technician.lastName}`,
              days: w.totalDays 
            }))
          });
        }
      }
    }
  }

  /**
   * Vérifier les recommandations pour les jours fériés
   * Note: Désactivé car compensation automatique
   */
  static checkHolidayRecommendations(week, weekOnCalls, holidays, results) {
    // Fonction désactivée - les compensations de jours fériés sont automatiques
    // Les jours fériés sont affichés à titre informatif seulement
  }

  /**
   * Générer des suggestions d'amélioration
   */
  static generateSuggestions(results) {
    // Compter les types de problèmes
    results.summary.criticalErrors = results.conflicts.length;
    results.summary.warnings = results.warnings.length;
    results.summary.suggestions = results.suggestions.length;

    // Ajouter des suggestions générales basées sur l'analyse
    if (results.summary.criticalErrors === 0 && results.summary.warnings === 0) {
      results.suggestions.push({
        type: 'COMPLIANCE_OK',
        severity: 'success',
        message: '✅ Aucun conflit critique détecté',
        details: 'Le planning semble conforme aux règles principales'
      });
    }

    if (results.summary.criticalErrors > 0) {
      results.suggestions.push({
        type: 'CRITICAL_ACTIONS_NEEDED',
        severity: 'critical',
        message: `⚠️ ${results.summary.criticalErrors} conflit(s) critique(s) à résoudre`,
        details: 'Ces conflits peuvent compromettre la continuité de service'
      });
    }
  }
}

export default ConflictDetectionService;
